/**
 * useAdminQueues — data-layer hooks for the four admin queues.
 *
 * Each hook returns { items, stats?, loading, error, refresh, bulk* }.
 * The `bulk*` functions POST to the corresponding backend endpoint and
 * return { processed, skipped? } so the caller can render a success toast.
 *
 * Backed by real Mongo:
 *   - useReviewQueue         (POST /api/admin/campaigns/*)
 *   - useVerificationQueue   (POST /api/admin/students/*)
 *   - useDisbursementQueue   (POST /api/admin/disbursements/* → Stripe Connect transfers)
 * Backed by mocked in-memory seed:
 *   - useModerationQueue     (POST /api/admin/moderation/*)
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mra_jwt";

const client = axios.create({ baseURL: API, withCredentials: true });
client.interceptors.request.use((cfg) => {
  const t = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  if (t) {
    cfg.headers = cfg.headers || {};
    cfg.headers.Authorization = `Bearer ${t}`;
  }
  return cfg;
});

const makeQueue = (fetchPath, actions) => () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await client.get(fetchPath);
      setItems(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load queue.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const wrapped = {};
  for (const [name, fn] of Object.entries(actions)) {
    wrapped[name] = async (...args) => {
      const res = await fn(client, ...args);
      await refresh();
      return res;
    };
  }
  return { items, loading, error, refresh, ...wrapped };
};

// ─── Campaign Review Queue (real) ────────────────────────────────────
export const useReviewQueue = makeQueue("/admin/campaigns/pending", {
  bulkApprove: async (client, campaignIds) => {
    const res = await client.post("/admin/campaigns/bulk-approve", { campaign_ids: campaignIds });
    return res.data;
  },
  bulkApproveEdits: async (client, campaignIds) => {
    const res = await client.post("/admin/campaigns/bulk-approve-edits", { campaign_ids: campaignIds });
    return res.data;
  },
  bulkFlag: async (client, campaignIds, flagType, note = null) => {
    const res = await client.post("/admin/campaigns/bulk-flag", {
      campaign_ids: campaignIds, flag_type: flagType, note,
    });
    return res.data;
  },
});

// ─── Student Verification Queue (real) ───────────────────────────────
export const useVerificationQueue = makeQueue("/admin/students/pending-verification", {
  bulkVerify: async (client, userIds) => {
    const res = await client.post("/admin/students/bulk-verify", { user_ids: userIds });
    return res.data;
  },
  bulkReject: async (client, userIds, reason) => {
    const res = await client.post("/admin/students/bulk-reject", { user_ids: userIds, reason });
    return res.data;
  },
});

// ─── Supporter Wall Moderation (mocked) ──────────────────────────────
export const useModerationQueue = makeQueue("/admin/moderation/flagged-messages", {
  bulkDismiss: async (client, messageIds) => {
    const res = await client.post("/admin/moderation/bulk-dismiss", { message_ids: messageIds });
    return res.data;
  },
  bulkRemove: async (client, messageIds) => {
    const res = await client.post("/admin/moderation/bulk-remove", { message_ids: messageIds });
    return res.data;
  },
});

// ─── Disbursements (real: Mongo grants + Stripe Transfers) ───────────
export const useDisbursementQueue = makeQueue("/admin/disbursements/pending", {
  bulkAuthorize: async (client, disbursementIds) => {
    const res = await client.post("/admin/disbursements/bulk-authorize", { disbursement_ids: disbursementIds });
    return res.data;
  },
  createGrant: async (client, body) => {
    const res = await client.post("/admin/disbursements", body);
    return res.data;
  },
  cancelGrant: async (client, grantId) => {
    const res = await client.post(`/admin/disbursements/${grantId}/cancel`);
    return res.data;
  },
});

export const useDisbursementMeta = () => {
  const [balance, setBalance] = useState(null);
  const [history, setHistory] = useState([]);
  const [options, setOptions] = useState([]);
  const [topups, setTopups] = useState([]);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    setLoading(true);
    const [b, h, o, t] = await Promise.allSettled([
      client.get("/admin/disbursements/balance"),
      client.get("/admin/disbursements/history"),
      client.get("/admin/disbursements/campaign-options"),
      client.get("/admin/disbursements/topups"),
    ]);
    setBalance(b.status === "fulfilled" ? b.value.data : null);
    setHistory(h.status === "fulfilled" ? h.value.data : []);
    setOptions(o.status === "fulfilled" ? o.value.data : []);
    setTopups(t.status === "fulfilled" ? t.value.data : []);
    setLoading(false);
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  const topUp = useCallback(async (body) => (await client.post("/admin/disbursements/topups", body)).data, []);
  return { balance, history, options, topups, loading, refresh, topUp };
};

// ─── Admin stats (shared across tabs) ────────────────────────────────
export const useAdminStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get("/admin/stats");
      setStats(res.data);
    } catch {
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  return { stats, loading, refresh: load };
};

// ─── Audit Log (read-only, paginated) ────────────────────────────────
export const useAuditLog = ({ action = null, targetType = null, limit = 50 } = {}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [hasMore, setHasMore] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { limit };
      if (action) params.action = action;
      if (targetType) params.target_type = targetType;
      const res = await client.get("/admin/audit-log", { params });
      setItems(res.data);
      setHasMore(res.data.length === limit);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load audit log.");
    } finally {
      setLoading(false);
    }
  }, [action, targetType, limit]);

  const loadMore = useCallback(async () => {
    if (!items.length || loadingMore) return;
    setLoadingMore(true);
    try {
      const params = { limit, before: items[items.length - 1].timestamp };
      if (action) params.action = action;
      if (targetType) params.target_type = targetType;
      const res = await client.get("/admin/audit-log", { params });
      setItems((prev) => [...prev, ...res.data]);
      setHasMore(res.data.length === limit);
    } finally {
      setLoadingMore(false);
    }
  }, [items, loadingMore, action, targetType, limit]);

  useEffect(() => { load(); }, [load]);

  return { items, loading, loadingMore, error, hasMore, refresh: load, loadMore };
};

/** Live roster for the read-only Homepage tab (lead is rule-based server-side). */
export const useLiveRoster = () => {
  const [live, setLive] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await client.get("/admin/campaigns/live");
      setLive(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load live campaigns.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  return { live, loading, error, refresh };
};

// ─── Extension requests (real) ───────────────────────────────────────
export const useExtensionQueue = makeQueue("/admin/extensions/pending", {
  approve: async (client, campaignId) => {
    const res = await client.post(`/admin/extensions/${campaignId}/approve`);
    return res.data;
  },
  reject: async (client, campaignId, reason) => {
    const res = await client.post(`/admin/extensions/${campaignId}/reject`, { reason });
    return res.data;
  },
});

/** Nightly lifecycle job — last run summary + manual trigger. */
export const useLifecycle = () => {
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);
  const refresh = useCallback(async () => {
    try {
      const res = await client.get("/admin/lifecycle");
      setStatus(res.data);
    } catch { /* card stays quiet */ }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  const runNow = useCallback(async () => {
    setRunning(true);
    try {
      const res = await client.post("/admin/lifecycle/run");
      await refresh();
      return res.data;
    } finally {
      setRunning(false);
    }
  }, [refresh]);
  return { status, running, refresh, runNow };
};

/** Backup history for the Backups tab — every run in the last `days` (nightly + manual). */
export const useBackupHistory = ({ days = 30, limit = 200 } = {}) => {
  const [rows, setRows] = useState([]);
  const [ttlDays, setTtlDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await client.get("/admin/backup/history", { params: { days, limit } });
      setRows(res.data.backups);
      setTtlDays(res.data.link_ttl_days);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load backup history.");
    } finally {
      setLoading(false);
    }
  }, [days, limit]);
  useEffect(() => { refresh(); }, [refresh]);
  return { rows, ttlDays, loading, error, refresh };
};

// ─── Wall moderation extras: every recent post + alert settings ──────
export const useRecentWallPosts = (limit = 50) => makeQueue(`/admin/moderation/recent-messages?limit=${limit}`, {
  remove: async (client, id) => (await client.post(`/admin/moderation/messages/${id}/remove`)).data,
  restore: async (client, id) => (await client.post(`/admin/moderation/messages/${id}/restore`)).data,
})();

export const useWallAlertSettings = () => {
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState("");
  const refresh = useCallback(async () => {
    try {
      setSettings((await client.get("/admin/moderation/alert-settings")).data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load alert settings.");
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  const save = async (patch) => {
    const res = await client.put("/admin/moderation/alert-settings", patch);
    setSettings(res.data);
    return res.data;
  };
  const sendDigestNow = async () => {
    const res = await client.post("/admin/moderation/digest/send-now");
    await refresh();
    return res.data;
  };
  return { settings, error, save, sendDigestNow, refresh };
};

// ─── Feedback surveys ────────────────────────────────────────────────
export const surveysAdmin = {
  config: async () => (await client.get("/admin/surveys/config")).data,
  saveConfig: async (patch) => (await client.put("/admin/surveys/config", patch)).data,
  summary: async (days) => (await client.get(`/admin/surveys/summary?days=${days}`)).data,
  students: async () => (await client.get("/admin/surveys/students")).data,
  waive: async (campaignId) => (await client.post(`/admin/surveys/students/${campaignId}/waive`)).data,
  sendDigestNow: async () => (await client.post("/admin/surveys/digest/send-now")).data,
  exportCsv: async () => {
    const res = await client.get("/admin/surveys/export.csv", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = Object.assign(document.createElement("a"), { href: url, download: "feedback-responses.csv" });
    a.click();
    URL.revokeObjectURL(url);
  },
};
